import { isBlockedAnalyticsPath, isMedicalGuide, MEDICAL_GUIDE_PATH } from "./analyticsPaths";
import { trackVibePoints } from "./vibePoints";
import { DEFAULT_LOCALE, getStoredLocale } from "../i18n/locales";

const API_BASE = (
  process.env.REACT_APP_API_BASE_URL || "https://api.vibe2nite.com"
).replace(/\/$/, "");

const STORAGE_KEY = "v2n_platform_session_id";
const LAST_PATH_KEY = "v2n_platform_last_path";
const USEFUL_SERVICE_PATHS = new Set([
  "/benalmadena-bus-times",
  "/benalmadena-train-times",
  "/benalmadena-taxis",
  "/benalmadena-to-malaga-airport",
  "/pharmacies-benalmadena",
  "/benalmadena-weather",
  "/markets-benalmadena",
  "/medical-help-benalmadena",
  "/emergency-help-benalmadena",
  "/tourist-information-benalmadena",
]);

function isQrResolverPath(pathname) {
  return pathname === "/go" || pathname.startsWith("/go/");
}

function createAnonymousSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `anon-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getAnonymousSessionId() {
  if (typeof window === "undefined") return "";

  try {
    const existing = window.localStorage.getItem(STORAGE_KEY);

    if (existing) return existing;

    const next = createAnonymousSessionId();
    window.localStorage.setItem(STORAGE_KEY, next);

    return next;
  } catch {
    return createAnonymousSessionId();
  }
}

function cleanPath(pathname) {
  const clean = String(pathname || "/").split("?")[0].split("#")[0];
  return clean || "/";
}

function getDeviceType() {
  if (typeof window === "undefined") return "unknown";

  const width = window.innerWidth || 0;

  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";

  return "desktop";
}

function getSelectedLocale() {
  return getStoredLocale() || DEFAULT_LOCALE;
}

function getUtmParams() {
  if (typeof window === "undefined") {
    return {};
  }

  const params = new URLSearchParams(window.location.search || "");

  return {
    utm_source: params.get("utm_source") || null,
    utm_medium: params.get("utm_medium") || null,
    utm_campaign: params.get("utm_campaign") || null,
    utm_content: params.get("utm_content") || null,
    utm_term: params.get("utm_term") || null,
  };
}

function getReferrer() {
  if (typeof document === "undefined") return null;

  try {
    if (!document.referrer) return null;

    const url = new URL(document.referrer);

    return `${url.origin}${url.pathname}`;
  } catch {
    return null;
  }
}

function getCityFromPath(pathname) {
  const path = cleanPath(pathname).toLowerCase();

  if (path.includes("torremolinos")) return "Torremolinos";
  if (path.includes("carihuela")) return "La Carihuela";
  if (path.includes("benalmadena") || path.includes("benalmádena")) {
    return "Benalmadena";
  }

  return null;
}

function getEventTypeFromPath(pathname) {
  const path = cleanPath(pathname).toLowerCase();

  if (path === "/") return "landing_view";
  if (path === "/vibe-pass") return "vibe_pass_page_view";
  if (path === "/useful-services") return "useful_services_page_view";
  if (USEFUL_SERVICE_PATHS.has(path)) return "useful_service_category_view";
  if (path === "/destinations") return "destination_view";
  if (path === "/venues") return "directory_view";
  if (path.startsWith("/venues/")) return "venue_page_view";
  if (path === "/awards" || path.startsWith("/awards/")) return "award_page_view";
  if (
    path.includes("restaurants") ||
    path.includes("bars") ||
    path.includes("beach-bars") ||
    path.includes("live-music") ||
    path.includes("clubs") ||
    path.includes("tapas")
  ) {
    return "category_view";
  }

  return "page_view";
}

function getVibePointsActionForPage(pathname) {
  const path = cleanPath(pathname).toLowerCase();

  if (path.includes("tonight")) return "events_view";

  return null;
}

function getVibePointsActionForEvent(eventType) {
  const map = {
    venue_view: "venue_view",
    directions_click: "directions_click",
    share_click: "share_click",
  };

  return map[eventType] || null;
}
function cleanAttributionValue(value, fallback = "") {
  const cleaned = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_.-]/g, "_")
    .slice(0, 80);

  return cleaned || fallback;
}

function inferSourceFromReferrer(referrer) {
  if (!referrer) return "direct";

  const value = String(referrer).toLowerCase();

  if (value.includes("facebook.com") || value.includes("fb.com")) {
    return "facebook";
  }

  if (value.includes("instagram.com")) {
    return "instagram";
  }

  if (value.includes("google.")) {
    return "google";
  }

  return "referral";
}

function inferMedium(source) {
  if (source === "facebook" || source === "instagram") {
    return "social";
  }

  if (source === "google") {
    return "organic";
  }

  if (source === "direct") {
    return "direct";
  }

  return "web";
}

function getPlatformAttribution() {
  if (typeof window === "undefined") {
    return {
      source: "direct",
      medium: "direct",
      campaign: "unknown",
    };
  }

  const params = new URLSearchParams(window.location.search || "");

  const utmSource = cleanAttributionValue(params.get("utm_source"));
  const utmMedium = cleanAttributionValue(params.get("utm_medium"));
  const utmCampaign = cleanAttributionValue(params.get("utm_campaign"));

  const source = utmSource || inferSourceFromReferrer(document.referrer);
  const medium = utmMedium || inferMedium(source);

  return {
    source,
    medium,
    campaign: utmCampaign || "unknown",
  };
}
export async function trackPlatformPageView(pathname) {
  if (typeof window === "undefined") return;

  const medical = isMedicalGuide(pathname);
  const pagePath = medical ? MEDICAL_GUIDE_PATH : cleanPath(pathname);

  if (isBlockedAnalyticsPath(pagePath) || isQrResolverPath(pagePath)) return;

  try {
    const lastPath = window.sessionStorage.getItem(LAST_PATH_KEY);

    if (lastPath === pagePath) return;

    window.sessionStorage.setItem(LAST_PATH_KEY, pagePath);
  } catch {
    // Ignore storage failures. Tracking should never affect navigation.
  }

  const attribution = medical ? {} : getPlatformAttribution();
  const payload = {
    anonymous_session_id: getAnonymousSessionId(),
    event_type: getEventTypeFromPath(pagePath),
    page_path: pagePath,
    city: getCityFromPath(pagePath),
    landing_page: pagePath,
    device_type: getDeviceType(),
    metadata_json: { locale: getSelectedLocale() },
    ...attribution,
    ...(medical ? {} : { referrer: getReferrer(), ...getUtmParams() }),
  };

  try {
    await fetch(`${API_BASE}/api/platform-analytics/track`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      keepalive: true,
      ...(medical ? { referrerPolicy: "no-referrer" } : {}),
      body: JSON.stringify(payload),
    });
  } catch {
    // Tracking must never break the user experience.
  }

  const pointsAction = getVibePointsActionForPage(pagePath);

  if (pointsAction) {
    void trackVibePoints(pointsAction, { page_path: pagePath });
  }
}
export async function trackPlatformEvent(eventType, params = {}) {
  if (typeof window === "undefined") return;

  const currentPath = window.location.pathname;
  if (isBlockedAnalyticsPath(currentPath)) return;
  const medical = isMedicalGuide(currentPath) || isMedicalGuide(params.page_path || "");
  const pagePath = medical ? MEDICAL_GUIDE_PATH : cleanPath(params.page_path || currentPath);
  if (isBlockedAnalyticsPath(pagePath)) return;

  const attribution = medical ? {} : getPlatformAttribution();
  const payload = {
    anonymous_session_id: getAnonymousSessionId(),
    event_type: eventType,
    page_path: pagePath,
    city: medical ? getCityFromPath(pagePath) : params.city || getCityFromPath(pagePath),
    ...(medical ? { category: "useful_services" } : {
      source: params.source || attribution.source,
      medium: attribution.medium,
      campaign: attribution.campaign,
      venue_id: params.venue_id || null,
      venue_name: params.venue_name || null,
      category: params.category || null,
    }),
    metadata_json: { ...(medical ? {} : params.metadata_json), locale: getSelectedLocale() },
  };

  try {
    await fetch(`${API_BASE}/api/platform-analytics/track`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      keepalive: true,
      ...(medical ? { referrerPolicy: "no-referrer" } : {}),
      body: JSON.stringify(payload),
    });
  } catch {
    // Tracking must never break the user experience.
  }

  const pointsAction = getVibePointsActionForEvent(eventType);

  if (pointsAction) {
    void trackVibePoints(pointsAction, {
      venue_id: payload.venue_id,
      page_path: pagePath,
    });
  }
}
